// @flow
import * as React from 'react';
import getDaysInMonth from 'date-fns/get_days_in_month';
import { css } from '@emotion/core';

import colors from '../../../Styles/colors';
import mq, {breakpoints} from '../../../Styles/breakpoints';
import { fontSize } from '../../../Styles/text';
import type {DateParts} from "../Container";
import {LargeCalendar} from "../Calendar";
import DateSelect from "./DateSelect";
import Channels from "./Channels";
import type {Channel} from "../../Objects";
import { event } from "./../../../Helpers/ga";
import {APP_NAME} from "../../App";

let fixedControls = css`
  position: fixed;
  width: calc(100% - 30px);
  margin-top: -3vw;
  padding-top: 3vw;
  background-color: ${colors.coreColor1};
  z-index: 4;
  
  @media(max-width: ${breakpoints.large - 1}px) {
    top: 0;
    visibility: hidden;
  }
  
  ${mq.xxlarge(`
    width: 855px;
  `)};
  
  @media print {
    position: static;
    margin: 0;
    padding: 0;
    width: 100%;
    visibility: visible;
    
    // Hack that seems to successfully target Safari. This is used because
    // Safari seems to be failing to constrain the grid to the width of the
    // printed page
    @media not all and (min-resolution:.001dpcm) { 
      @media {
        max-width: 720px;  
      }
    }
  }
`;

let spacer = css`
  background-color: ${colors.coreColor1};
  z-index: 4;
  
  @media(max-width: ${breakpoints.large - 1}px) {
    visibility: visible;
    position: sticky;
    top: 0;
    padding-top: 4px;
    padding-bottom: 8px;
  }
  
  ${mq.large(`
    visibility: hidden;
  `)}
  
  ${mq.xxlarge(`
    width: 855px;
  `)};
  
  @media print {
    display: none;
  }
`;

let controls = css`
  padding-bottom: 8px;
  ${fontSize(4)};
  display: flex;
  align-items: center;
  justify-content: space-between;
  z-index: 1;
  
  @media print {
    justify-content: center;
  }
`;

let dayButton = css`
  margin: 0;
  padding: 0;
  background: none;
  ${fontSize(5)};
  font-weight: bold;
  display: flex;
  align-items: center;
  transition-property: color;
  transition-duration: 300ms;
  transition-timing-function: ease-out;
    
  &:hover, &:active, &:focus {
    background: none;
    color: ${colors.accent};
  }
  
  @media print {
    display: none;
  }
`;

let largeCalWrapper = css`
  @media(max-width: ${breakpoints.large-1}px) {
    display: none;
  }

  ${mq.xxlarge(`
    display: none;
  `)}
`;

let largeCalendarHolder = css`
  margin-bottom: 8px;
  padding: 8px;
  background: #ccc;
  display: flex;
  align-items: center;
  justify-content: center;
`;

type Props = {|
  loadPrevDay: () => Promise<any>,
  loadNextDay: () => Promise<any>,
  loadDate: (year: number, month: number, date: number) => Promise<any>,
  current: DateParts,
  channelList: Array<Channel>,
  calendar: React.Element<typeof LargeCalendar>,
  calendarMonth: number,
  calendarYear: number,
  changeChannel: (index: number) => void,
  currentChannel: number
|};

type State = {|
  isCalendarOpen: bool
|};

export class Controls extends React.PureComponent<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      isCalendarOpen: false
    };
  }

  toggleCalendar() {
    this.setState({
      isCalendarOpen: !this.state.isCalendarOpen
    });
  }

  selectDate(e: SyntheticInputEvent<HTMLSelectElement>) {
    let parts = e.target.value.split('-');

    if (parts.length === 3) {
      let year = parseInt(parts[0]);
      let month = parseInt(parts[1]);
      let date = parseInt(parts[2]);

      this.props.loadDate(year, month, date);
    }
  }

  nextDay() {
    let {
      loadNextDay,
      current: {
        year,
        month,
        date
      }
    } = this.props;

    event(APP_NAME, 'Click Next Day Button', `${year}-${month}-${date}`);
    loadNextDay();
  }

  prevDay() {
    let {
      loadPrevDay,
      current: {
        year,
        month,
        date
      }
    } = this.props;

    event(APP_NAME, 'Click Prev Day Button', `${year}-${month}-${date}`);
    loadPrevDay();
  }

  renderControls(active: bool) {
    let {
      channelList,
      current: {
        year,
        month,
        date
      },
      calendar,
      calendarMonth,
      calendarYear,
      changeChannel,
      currentChannel,
    } = this.props;

    let { isCalendarOpen } = this.state;

    let days = getDaysInMonth(new Date(calendarYear, calendarMonth));
    let offset = (new Date(calendarYear, calendarMonth, 1)).getDay() + 1;
    let maxRows = Math.ceil((offset + days - 1) / 7);

    let height = maxRows === 6 ? '234px' : '209px';

    let controlCSS;

    if (active) {
      controlCSS = fixedControls;
    } else {
      controlCSS = spacer
    }

    return <div aria-hidden={!active} css={controlCSS}>
      {/* Controls */}
      <div css={controls}>
        <button css={dayButton} onClick={this.prevDay.bind(this)} aria-label="Previous Day">
          <i className="fal fa-2x fa-angle-left"/>
          <span className="show-for-large-up">{'\u00a0\u00a0'}</span>
          <span className="hide-for-large-up">{'\u00a0'}</span>
          Prev
          <span className="show-for-large-up">{'\u00a0'}Day</span>
        </button>
        <DateSelect
          year={year}
          month={month}
          date={date}
          onChange={this.selectDate.bind(this)}
          toggleCalendar={this.toggleCalendar.bind(this)}
          isCalendarOpen={isCalendarOpen}
        />
        <button css={dayButton} onClick={this.nextDay.bind(this)} aria-label="Next Day">
          Next
          <span className="show-for-large-up">{'\u00a0'}Day{'\u00a0\u00a0'}</span>
          <span className="hide-for-large-up">{'\u00a0'}</span>
          <i className="fal fa-2x fa-angle-right"/>
        </button>
      </div>
      <div css={largeCalWrapper}>
        <div css={largeCalendarHolder} style={{display: isCalendarOpen ? 'flex' : 'none', height: height}}>
          { isCalendarOpen && calendar }
        </div>
      </div>
      <Channels channelList={channelList} current={currentChannel} changeChannel={changeChannel} />
    </div>;
  }

  render() {
    return <React.Fragment>
      {this.renderControls(true)}
      {this.renderControls(false)}
    </React.Fragment>;
  }
}